import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Check, Loader2 } from "lucide-react";
import "./TeamPawsPersonalityEditPage.css";
import progressIcon from "../../assets/progressIcon5.png";
import nextIcon from "../../assets/nextButtonIcon.png";
import nextIcon2 from "../../assets/nextButtonIcon2.png";
import apiClient from "../../api/apiClient";
import MobileTopBar from "../../components/MobileTopBar";
import MobileBottomBar from "../../components/MobileBottomBar";
import DesktopTopBar from "../../components/DesktopTopBar";
import { useGlobalAlert } from "../../context/AlertContext";

const TeamPawsPersonalityEditPage = () => {
  const navigate = useNavigate();
  const { profile } = useSelector((state) => state.user);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  const { triggerAlert } = useGlobalAlert();

  // Mobile Step State (1 for Left Panel, 2 for Right Panel)
  const location = useLocation();
  const [mobileStep, setMobileStep] = useState(location.state?.step || 1);

  const [formData, setFormData] = useState({
    dogName: "",
    selectedTraits: [],
    selectedHobbies: [],
    customPastimes: [""],
  });

  const pastimeOptions = [
    "🚶 Walks & exploring",
    "🌲 Hiking & adventures",
    "🏃 Running & fitness",
    "🎾 Playing & fetch",
    "🐕 Dog parks & playdates",
    "🏖️ Beach & swimming",
    "🚗 Car rides & day trips",
    "☕ Cafés & hanging out",
    "🛋️ Cuddling & relaxing",
  ];

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const res = await apiClient.get(`/user/details/${profile?.id}`);
        if (res.data?.status && res.data.data) {
          const userData = res.data.data;
          setUser(userData);

          if (userData.dogs && userData.dogs.length > 0) {
            const dog = userData.dogs[0];

            const fetchedTraits = Array.isArray(dog.dog_traits)
              ? dog.dog_traits
              : [];
            const fetchedHobbies = Array.isArray(dog.dog_hobbies)
              ? dog.dog_hobbies
              : [];

            const custom = fetchedHobbies.filter(
              (h) => !pastimeOptions.includes(h),
            );

            const initialCustom = [...custom];
            if (
              initialCustom.length === 0 ||
              initialCustom[initialCustom.length - 1] !== ""
            ) {
              initialCustom.push("");
            }

            setFormData({
              dogName: dog.dog_name || "",
              selectedTraits: fetchedTraits,
              selectedHobbies: fetchedHobbies,
              customPastimes: initialCustom,
            });
          }
        }
      } catch (err) {
        console.error("Error fetching user:", err);
      } finally {
        setLoading(false);
      }
    };
    if (profile?.id) fetchUserDetails();
  }, [profile?.id]);

  const dogName = formData.dogName || "Dog";

  // Handlers
  const toggleItem = (item, listField) => {
    const currentList = formData[listField] || [];
    const isSelected = currentList.includes(item);

    // Limit to 3 choices
    if (!isSelected && currentList.length >= 3) {
      if (triggerAlert) {
        triggerAlert("You can only select up to 3 hobbies.", "warning");
      }
      return;
    }

    const newList = isSelected
      ? currentList.filter((i) => i !== item)
      : [...currentList, item];

    setFormData((prev) => ({ ...prev, [listField]: newList }));
  };

  const handleCustomWriteOwn = (value, index) => {
    const newCustomPastimes = [...(formData.customPastimes || [""])];
    const oldValue = newCustomPastimes[index];

    let updatedSelected = (formData.selectedHobbies || []).filter(
      (item) => item !== oldValue,
    );

    if (value.trim() !== "") {
      // Limit custom input addition to the max 3 rule
      if (
        (!oldValue || oldValue.trim() === "") &&
        updatedSelected.length >= 3
      ) {
        if (triggerAlert) {
          triggerAlert("You can only select up to 3 hobbies.", "warning");
        } else {
          window.alert("You can only select up to 3 hobbies.");
        }
        return;
      }
      updatedSelected = [...updatedSelected, value];
    }

    newCustomPastimes[index] = value;

    if (index === newCustomPastimes.length - 1 && value.trim() !== "") {
      newCustomPastimes.push("");
    }

    setFormData((prev) => ({
      ...prev,
      customPastimes: newCustomPastimes,
      selectedHobbies: updatedSelected,
    }));
  };

  const clearCustomHobby = (index) => {
    const newCustomPastimes = [...(formData.customPastimes || [])];
    const oldValue = newCustomPastimes[index];

    if (!oldValue) return;

    let updatedSelected = (formData.selectedHobbies || []).filter(
      (item) => item !== oldValue,
    );

    newCustomPastimes.splice(index, 1);

    if (
      newCustomPastimes.length === 0 ||
      newCustomPastimes[newCustomPastimes.length - 1] !== ""
    ) {
      newCustomPastimes.push("");
    }

    setFormData((prev) => ({
      ...prev,
      customPastimes: newCustomPastimes,
      selectedHobbies: updatedSelected,
    }));
  };

  // Validation
  const isStep1Valid = formData.selectedTraits?.length >= 1;
  const isStep2Valid = (formData.selectedHobbies || []).length >= 1;
  const isNextEnabled = isStep1Valid && isStep2Valid;

  const handleNext = async () => {
    if (!isNextEnabled || isUpdating) return;
    setIsUpdating(true);
    try {
      const dogId = user?.dogs?.[0]?.id;
      const apiPayload = {
        user_id: profile?.id,
        dog_traits: JSON.stringify(formData.selectedTraits),
        dog_hobbies: JSON.stringify(formData.selectedHobbies),
      };

      const response = await apiClient.put(
        `/dogs/update-dog-by-id/${dogId}`,
        apiPayload,
      );
      if (response.data?.status) navigate("/edit-dimsum-availability");
    } catch (error) {
      console.error("Update failed:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSkip = () => {
    navigate("/edit-dimsum-availability");
  };

  const formattedDogName =
    dogName.charAt(0).toUpperCase() + dogName.slice(1).toLowerCase();

  if (loading) {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background:
            "linear-gradient(157.14deg, #96f2de 2.76%, #58b8a3 96.76%)",
          zIndex: 9999,
        }}
      >
        <Loader2 className="animate-spin" size={40} />
      </div>
    );
  }

  return (
    <div
      className={`tppe-container ${mobileStep === 1 ? "tppe-mobile-step-1" : "tppe-mobile-step-2"}`}
    >
      {/* LEFT PANEL: Traits Paragraph (Mobile Step 1) */}
      <div
        className={`tppe-panel tppe-left-panel ${mobileStep === 1 ? "tppe-mobile-active" : "tppe-mobile-hidden"}`}
      >
        <MobileTopBar
          onBack={() => {
            if (window.innerWidth < 1024) {
              navigate("/edit-dog-personality", {
                state: { step: 2 },
              });
            } else {
              navigate(-1);
            }
          }}
          backColor="#000"
          logoColor="#009fd9"
          blueLogo="true"
        />

        <DesktopTopBar
          showBack={true}
          onBack={() => navigate("/edit-dog-personality")}
          backColor="#000"
        />

        <div className="tppe-content-wrapper">
          {/* Header & Progress */}
          <div className="tppe-header-section">
            <div className="tppe-dog-icon-container">
              <img
                src={progressIcon}
                alt="Progress Step"
                className="tppe-progress-icon"
                style={
                  window.innerWidth <= 1023
                    ? { height: "60px", marginLeft: "-1rem" }
                    : {}
                }
              />
            </div>
            <div className="tppe-progress-track">
              <div className="tppe-progress-fill tppe-step-1-fill"></div>
            </div>
          </div>

          <h1 className="tppe-title" style={{ textAlign: "left" }}>
            DESCRIBE {dogName.toUpperCase()}'S IN 3 WORDS.
          </h1>
          <p
            className="tppe-subtitle"
            style={{ textAlign: "left", marginBottom: "1.5rem" }}
          >
            Give us a glimpse into their pawsonality!
          </p>

          <textarea
            className="tppe-personality-textarea"
            placeholder="Goofy, cuddly, adventurous..."
            value={(formData.selectedTraits || []).join(", ")}
            onChange={(e) => {
              const val = e.target.value;
              const arr = val ? [val] : [];
              setFormData((prev) => ({
                ...prev,
                selectedTraits: arr,
              }));
            }}
          />

          <MobileBottomBar
            onNext={() => isStep1Valid && setMobileStep(2)}
            isNextEnabled={isStep1Valid}
            nextIconSrc={nextIcon2}
            showSkip={true}
            isSkipBlue={true}
            skipText="EXIT"
            onSkip={() => navigate("/user-profile")}
            variant="blue"
          />
        </div>
      </div>

      {/* RIGHT PANEL: Pastimes (Mobile Step 2) */}
      <div
        className={`tppe-panel tppe-right-panel ${mobileStep === 2 ? "tppe-mobile-active" : "tppe-mobile-hidden"}`}
      >
        <MobileTopBar
          onBack={() => setMobileStep(1)}
          backColor="#fff"
          logoColor="#fff"
        />

        <div className="tppe-content-wrapper tppe-right-content">
          {/* Mobile Only Progress (Mirrored) */}
          <div className="tppe-header-section tppe-mobile-only">
            <div className="tppe-dog-icon-container">
              <img
                src={progressIcon}
                alt="Progress"
                className="tppe-progress-icon"
                style={{ height: "65px", marginLeft: "-1rem" }}
              />
            </div>
            <div className="tppe-progress-track">
              <div className="tppe-progress-fill tppe-step-2-fill"></div>
            </div>
          </div>

          <div className="tppe-text-group">
            <h1
              className="tppe-title tppe-text-white"
              style={{ textAlign: "center" }}
            >
              WHAT ARE {dogName.toUpperCase()}'S TOP 3 HOBBIES?
            </h1>
            <p
              className="tppe-subtitle tppe-text-white-sub"
              style={{ marginBottom: "1rem" }}
            >
              What would {formattedDogName}'s hobby list look like if he could
              only pick a few?
            </p>
            <p
              className="tppe-subtitle tppe-text-white-sub"
              style={{ textAlign: "center", marginBottom: "1.5rem" }}
            >
              Choose up to 3.
            </p>
          </div>

          <div className="tppe-list-container tppe-hobbies-list">
            {pastimeOptions.map((pastime, index) => {
              const isChecked = (formData.selectedHobbies || []).includes(
                pastime,
              );
              return (
                <div
                  key={index}
                  className="tppe-option-row tppe-border-light"
                  onClick={() => toggleItem(pastime, "selectedHobbies")}
                >
                  <span className="tppe-option-label tppe-text-white">
                    {pastime}
                  </span>
                  <div
                    className={`tppe-checkbox ${isChecked ? "checked" : ""}`}
                  >
                    {isChecked && (
                      <Check size={18} strokeWidth={4} color="#000" />
                    )}
                  </div>
                </div>
              );
            })}

            {(formData.customPastimes || [""]).map((customVal, index) => (
              <div
                key={`custom-hobby-${index}`}
                className="tppe-write-own-box tppe-bg-light-blue"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  paddingRight: "15px",
                  marginTop: index === 0 ? "1.5rem" : "0.75rem",
                }}
              >
                <input
                  type="text"
                  placeholder="Other: ______"
                  className="tppe-write-own-input"
                  value={customVal}
                  onChange={(e) => handleCustomWriteOwn(e.target.value, index)}
                />
                {customVal.trim() && (
                  <div
                    className="tppe-checkbox checked"
                    onClick={() => clearCustomHobby(index)}
                    style={{ cursor: "pointer" }}
                  >
                    <Check size={18} strokeWidth={4} color="#000" />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Footer Navigation (Desktop + Mobile Step 2) */}
          <div className="tppe-footer-nav tppe-step-2-next desktop-only">
            {/* Desktop Only Skip */}
            <button
              className="tppe-skip-btn tppe-desktop-only"
              onClick={handleSkip}
            >
              SKIP
            </button>

            <button
              type="button"
              className="tppe-skip-btn"
              onClick={() => navigate("/user-profile")}
            >
              EXIT
            </button>

            <button
              className={`tppe-next-btn-group ${
                window.innerWidth >= 1024
                  ? isNextEnabled && !isUpdating
                    ? "enabled"
                    : "disabled"
                  : isStep2Valid && !isUpdating
                    ? "enabled"
                    : "disabled"
              }`}
              onClick={handleNext}
              disabled={isUpdating}
            >
              <span
                className="tppe-next-text"
                style={window.innerWidth < 1024 ? { color: "#47d7ac" } : {}}
              >
                {isUpdating ? "UPDATING..." : "NEXT"}
              </span>
              <div
                className="tppe-next-circle"
                style={
                  window.innerWidth < 1024
                    ? { backgroundColor: "rgba(71, 215, 172, 0.15)" }
                    : {}
                }
              >
                {isUpdating ? (
                  <Loader2 className="animate-spin" color="white" />
                ) : (
                  <img src={nextIcon} alt="Next" className="tppe-next-img" />
                )}
              </div>
            </button>
          </div>
          <MobileBottomBar
            onNext={handleNext}
            isNextEnabled={isStep2Valid && !isUpdating}
            nextIconSrc={nextIcon}
            showSkip={true}
            skipText="EXIT"
            onSkip={() => navigate("/user-profile")}
            variant="green"
          />
        </div>
      </div>
    </div>
  );
};

export default TeamPawsPersonalityEditPage;
